import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { ChromiumDialog } from '@chancenwerk/client/design/components/cr-dialog.js';
import { ChromiumSnackbar } from '@chancenwerk/client/design/components/cr-snackbar.js';
import { ContactRequestAPI } from '@chancenwerk/db/api/ContactRequestAPI.js';
import { ContactRequest } from '@chancenwerk/db/ContactRequest.js';
import { LogistikSettings } from '@chancenwerk/db/LogistikClearanceSettings.js';
import { LogistikSchoolAccessToken } from '@chancenwerk/db/tokens.js';
import { LogistikDialogDisallowedOrderChange } from '@chancenwerk/logistik/client/components/dialogs/logistik-dialog-disallowed-order-change.js';
import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { map, switchMap } from 'rxjs';
import { activeToken } from '../globalSubjects.js';

/**
 * @customElement hefte-dialog-disallowed-order-change
 */
@customElement('hefte-dialog-disallowed-order-change')
export class HefteDialogDisallowedOrderChange extends LogistikDialogDisallowedOrderChange {
  static override get element_name() {
    return 'hefte-dialog-disallowed-order-change';
  }

  @state() protected readonly _settings = new RXController(
    this,
    LogistikSettings.get()({ maxOrderAmountSupportEmail: 1 }),
  );

  override content() {
    if (this._settings.isLoading) {
      return html`<cr-loader></cr-loader>`;
    }
    if ((this.allowedChange.type === 'add' && this.allowedChange.lower) || this.allowedChange.type === 'none') {
      return html`
        <cr-text>
          Bei der von Ihnen gewünschten Bestellmenge müssen wir sicherheitshalber individuell prüfen, ob wir Ihren
          Wunsch erfüllen können. <br />
          Bitte sprechen Sie uns an! <br />
          Vielen Dank!
        </cr-text>
      `;
    } else {
      return super.content();
    }
  }

  override footerContent() {
    return html`
      <cr-flex horizontal end space="sm">
        <cr-button @click=${() => this.resolve(false)}> Abbrechen </cr-button>
        ${this.allowedChange.type === 'add' && this.allowedChange.quantity > 0
          ? html` <cr-button @click=${() => this.resolve(true)}> ${this.allowedChange.quantity} Bestellen</cr-button> `
          : html` <cr-button @click=${() => this.resolve(false)}> Schließen </cr-button> `}
        ${this._settings.value?.maxOrderAmountSupportEmail
          ? html`<cr-button @click=${() => this._handleContactSupport()}> Kontaktieren </cr-button>`
          : nothing}
      </cr-flex>
    `;
  }

  private async _handleContactSupport() {
    await HefteDialogContactInformationForm.showPopup<boolean>({})
      .then(result => {
        if (result) {
          ChromiumSnackbar.show('Die Anfrage wurde versandt');
        } else {
          ChromiumSnackbar.show('Fehler beim Versenden der Anfrage');
        }
        this.resolve(false); // False because we don't want to proceed with the order
      })
      .catch(reason => {
        ChromiumSnackbar.show('Es wurde keine Anfrage versandt');
        this.reject(reason);
      });
  }
}

/**
 * @customElement hefte-dialog-contact-information-form
 */
@customElement('hefte-dialog-contact-information-form')
export class HefteDialogContactInformationForm extends ChromiumDialog<boolean> {
  static override get element_name(): string {
    return 'hefte-dialog-contact-information-form';
  }

  @state() private _tokenInfo = new RXController(this, activeToken, token$ =>
    token$.pipe(
      switchMap(token =>
        LogistikSchoolAccessToken.getByToken<LogistikSchoolAccessToken>(+token)(LogistikSchoolAccessToken.allFields),
      ),
    ),
  );

  @state() private readonly form = new FormController(
    this,
    this._tokenInfo.observable.pipe(map(token => new ContactRequest({ tokenID: token._id }))),
    {
      success: async entity => {
        const id = await ChromiumDialog.waitFor(
          ContactRequestAPI.requestContact(entity.email, entity.tel, entity.tokenID)(),
        );
        this.resolve(!!id);
      },
    },
  );

  override heading?: string | undefined = 'Kontaktdaten';

  override content() {
    return html`
      <cr-flex space="md">
        <cr-text> Bitte geben Sie Ihre Kontaktdaten an. </cr-text>
        <cr-input placeholder="E-Mail-Adresse" type="email" ${this.form.field('email')}> </cr-input>
        <cr-text> oder </cr-text>
        <cr-input placeholder="Telefonnummer" type="tel" ${this.form.field('tel')}> </cr-input>
      </cr-flex>
    `;
  }

  override footerContent() {
    return html`
      <cr-flex horizontal end space="sm">
        <cr-button @click=${() => this.reject('cancelled')}>Abbrechen</cr-button>
        <cr-button @click=${() => this.form.validate()}>Senden</cr-button>
      </cr-flex>
    `;
  }
}
